import React from 'react';
import {useFlag} from '@sail/access';
import {css, RootLayer} from '@sail/ui/deprecated';
import {
  view,
  css as cssBeta,
  UiProvider,
  IframeScrollbarStyleProvider,
  DeprecatedPopoverContext,
  FocusContainmentProvider,
} from '@sail/ui';
import type {RequestIframe} from '@sail/ui';
import type {IThemingContext} from '@stripe-internal/connect-embedded-lib';
import type {AppearanceWithDefaults} from '@stripe-internal/embedded-theming';
import {
  getAppearanceValuesWithDefaults,
  mapAppearanceOptionsToDesignTokens,
} from '@stripe-internal/embedded-theming';
import {provider, VIEW_CLASS} from '@sail/engine';
import {getThemeOverrides} from '../../../connect/theming';
import {useConnectJsOptionsContext} from '../../../data-layer-client/ConnectJsOptionsContext/useConnectJsOptionsContext';
import {getAllUILayers} from '../../../ui-layer-frame/utils/frameUtils';
import {ScrollbarStyleOverrides} from '../ScrollbarStyleOverrides';
import {
  CONNECT_ELEMENT_IMPORTS,
  type ConnectElementImportKeys,
} from '../../../connect/ConnectJSInterface/ConnectElementList';
import {useShouldRenderPlaceholderBottomSheets} from '../../../ui-layer-frame/utils/mobileUtils';

export const ThemeContext = React.createContext<{
  appearanceWithDefaults: AppearanceWithDefaults;
  values: IThemingContext;
  ignoreThemingValues: boolean;
}>({
  appearanceWithDefaults: getAppearanceValuesWithDefaults({}),
  values: {},
  ignoreThemingValues: false,
});
ThemeContext.displayName = 'ThemeContext';

export const useThemeContext = () => React.useContext(ThemeContext);

const SAIL_NEXT_TOKEN_CLASS = 'sn-token-provider';

export type Props = {
  children: React.ReactNode;
  ignoreThemingValues: boolean;
  componentControlledBackground: boolean | undefined;
  requestIframe?: RequestIframe;
  component: ConnectElementImportKeys;
};

export const ThemeProvider: React.FC<Props> = ({
  children,
  ignoreThemingValues,
  componentControlledBackground,
  requestIframe,
  component,
}) => {
  const {
    values: {appearance: inputAppearance, metaOptions},
  } = useConnectJsOptionsContext();

  const shouldRenderPlaceholderBottomSheets =
    useShouldRenderPlaceholderBottomSheets(component);
  const mobileUIEnabledForComponent =
    CONNECT_ELEMENT_IMPORTS[component]?.applyMobileUIOverrides;

  // See MobileUIOverrides.tsx for explanation on flag usage
  const enableSailBottomSheets = useFlag('enable_sail_bottom_sheets');
  const enableSailBottomSheetsPhaseOne =
    useFlag('sail_bottom_sheets_phase_one') && mobileUIEnabledForComponent;
  const enableSailBottomSheetsPhaseTwo = useFlag(
    'sail_bottom_sheets_phase_two',
  );

  const enableBottomSheets =
    enableSailBottomSheets ||
    enableSailBottomSheetsPhaseOne ||
    enableSailBottomSheetsPhaseTwo;

  // Disable the stable popover (and use the deprecated one)
  // if bottom sheets are disabled
  const disableStablePopover = !enableBottomSheets;

  const appearance = React.useMemo(
    () =>
      ignoreThemingValues
        ? {
            variables: {},
          }
        : inputAppearance,
    [ignoreThemingValues, inputAppearance],
  );

  // TODO: Once we are migrated fully to sail-next beta, we can delete the legacy theme overrides
  const legacyThemeOverrides = getThemeOverrides(
    ignoreThemingValues,
    appearance,
  );

  const themingDesignTokens = React.useMemo(
    () => mapAppearanceOptionsToDesignTokens(appearance, metaOptions.mobileSdk),
    [appearance, metaOptions.mobileSdk],
  );

  const themingUses = [
    ...themingDesignTokens, // Theming parameters
    cssBeta.global({
      // This element is included at the root of every shadow DOM we have. Having it there ensures test color default value is always inherited
      '.sn-token-provider': {
        color: 'text' as const,
      },
      // Used to override the hardcoded font-family here: https://git.corp.stripe.com/stripe-internal/pay-server/blob/b90bc29fd323cff30ee7dad5a95c4cb4a4f712f4/frontend/sail-next/packages/ui/src/css/css.ts#L24
      // See: https://jira.corp.stripe.com/browse/SAIL-3433
      '@supports (-webkit-hyphens:none)': {
        // The double VIEW_CLASS selector is necessary to increase the specificity of the rule
        [`select.${VIEW_CLASS}.${VIEW_CLASS}`]: {
          fontFamily: 'inherit !important',
        },
      },
    }),
  ];

  return (
    <ThemeContext.Provider
      value={{
        appearanceWithDefaults: getAppearanceValuesWithDefaults(appearance),
        values: appearance,
        ignoreThemingValues,
      }}
    >
      <RootLayer
        uses={[css.token.assign(legacyThemeOverrides)]}
        css={{
          fontFamily: css.value(appearance.variables?.fontFamily || 'inherit'),
        }}
      >
        <view.div
          className={SAIL_NEXT_TOKEN_CLASS}
          uses={[...themingUses]}
          css={{
            ...(componentControlledBackground
              ? {}
              : {
                  backgroundColor: appearance?.variables?.colorBackground
                    ? 'surface'
                    : 'transparent', // The background color for an embedded component should be transparent, and only use the surface token if a colorBackground override is set.
                }),
          }}
        >
          <view.div
            uses={[
              provider(DeprecatedPopoverContext, {
                useDeprecatedPopoverComponents: disableStablePopover,
              }),
            ]}
          >
            <IframeScrollbarStyleProvider
              scrollbarStyleOverrides={ScrollbarStyleOverrides}
            >
              <FocusContainmentProvider>
                <UiProvider
                  _unstable_requestIframe={requestIframe}
                  _unstable_getUILayers={getAllUILayers}
                  _unstable_shouldSkipIframePortal={
                    shouldRenderPlaceholderBottomSheets
                  }
                  enableBottomSheets={enableBottomSheets}
                  disableStablePopover={disableStablePopover}
                >
                  <ScrollbarStyleOverrides>{children}</ScrollbarStyleOverrides>
                </UiProvider>
              </FocusContainmentProvider>
            </IframeScrollbarStyleProvider>
          </view.div>
        </view.div>
      </RootLayer>
    </ThemeContext.Provider>
  );
};
